import axios from 'axios';
import { Message, MessageBox } from 'element-ui';
import router from '@/router';

// 环境识别工具函数（迁移自原axios.js）
const isWxWorkEnv = () => {
  const ua = navigator.userAgent.toLowerCase();
  // 匹配企业微信桌面端 + 移动端 UA
  return ua.includes('wxwork') || ua.includes('wechatwork');
};

const service = axios.create({
  baseURL: isWxWorkEnv() 
  //  ? 'https://gslhys.com/api/wxwork'  // 企业微信环境的接口前缀
    // : 'http://127.0.0.1:8080/api',        // 普通浏览器的接口前缀
  ? '/api/wxwork' : '/api', 
  timeout: isWxWorkEnv() ? 30000 : 15000,
  withCredentials: true,
  headers: {
    'X-Env-Type': isWxWorkEnv() ? 'wxwork' : 'browser',
    'Content-Type': 'application/json;charset=utf-8',
    'User-Agent': isWxWorkEnv() ? 'WeChatWork/Mobile' : 'WebBrowser'
  },
  // 【已删除】无效的全局cancelToken配置
  validateStatus: (status) => {
    return status >= 200 && status < 600;
  }
});

// 新增：请求防抖/重复请求拦截（核心解决多次点击导致的请求阻塞）
const pendingRequests = new Map();
const getRequestKey = (config) => {
  const { method, url, params, data } = config;
  return [method, url, JSON.stringify(params), JSON.stringify(data)].join('|');
};

// 请求拦截器：新增重复请求拦截+完善blob兼容+兜底配置
service.interceptors.request.use(
  config => {
    // 1. 重复请求拦截：相同请求未完成时，取消前一个请求
    const requestKey = getRequestKey(config);
    if (pendingRequests.has(requestKey)) {
      pendingRequests.get(requestKey)('取消重复请求');
      pendingRequests.delete(requestKey);
    }
    config.cancelToken = new axios.CancelToken(cancel => {
      pendingRequests.set(requestKey, cancel);
    });

    // 2. blob类型请求（Excel导出）专属兼容处理（保留并完善）
    if (config.responseType === 'blob') {
      // 强制删除冲突头，避免后端解析异常
      delete config.headers['Content-Type'];
      delete config.headers['User-Agent'];
      // 强制保留必要配置，防止被覆盖
      config.headers['X-Env-Type'] = isWxWorkEnv() ? 'wxwork' : 'browser';
      config.withCredentials = true;
      config.responseType = 'blob'; // 强制指定，防止被篡改
    }

    // 3. Token逻辑：优先从sessionStorage读取，完善兜底
    const token = sessionStorage.getItem('accessToken') || localStorage.getItem('accessToken') || '';
    if (token) {
      config.headers['Authorization'] = `Bearer ${token.trim()}`;
    }

    // 4. 调试日志：仅开发环境打印，避免生产环境性能损耗
    if (process.env.NODE_ENV === 'development') {
      console.log('【请求配置】', config.url, config.headers);
    }

    return config;
  },
  error => {
    // 新增：请求拦截器异常必兜底，确保Promise不挂起
    console.error('【请求拦截器异常】', error);
    // 清除pending请求，防止内存泄漏
    pendingRequests.clear();
    Message.error('请求初始化失败，请刷新页面重试');
    // 强制返回reject，确保页面catch能捕获
    return Promise.reject(new Error('请求拦截器异常：' + error.message));
  }
);

// 响应拦截器：核心重构——全异常兜底+Promise必状态+清除pending请求
service.interceptors.response.use(
  response => {
    // 1. 立即清除当前请求的pending标记，防止内存泄漏
    const requestKey = getRequestKey(response.config);
    pendingRequests.delete(requestKey);

    // 2. blob类型请求（Excel导出）：直接返回完整响应，不做任何解析（保留）
    if (response.config.responseType === 'blob') {
      // 新增：blob空数据兜底，防止页面处理时报错
      if (!response.data || response.data.size === 0) {
        Message.warning('导出数据为空，下载失败');
        return Promise.reject(new Error('blob数据为空'));
      }
      return response;
    }

    // 3. JSON类型请求：多层硬核兜底，防止任何解析/类型错误
    try {
      // 第一层兜底：response.data为空时，强制赋值默认值
      let res = response.data || { code: 200, data: {}, msg: '请求成功' };
      // 第二层兜底：确保res是对象，防止后端返回字符串/数组
      if (typeof res !== 'object' || Array.isArray(res)) {
        res = { code: 200, data: res, msg: '数据格式异常，已兜底' };
      }
      // 第三层兜底：确保code为有效数字，msg为字符串
      res.code = Number.isFinite(+res.code) ? +res.code : 200;
      res.msg = res.msg || (res.code === 200 ? '请求成功' : '业务处理失败');
      // 第四层兜底：确保data存在，防止页面解构时报错
      res.data = res.data || {};

      // 4. 401 Token过期：单独处理，强制跳转（保留原有逻辑并完善）
      if (res.code === 401) {
        // 清空所有Token和用户信息，防止残留
        localStorage.removeItem('accessToken');
        localStorage.removeItem('adminName');
        sessionStorage.removeItem('accessToken');
        sessionStorage.removeItem('adminName');
        // 避免重复弹出确认框
        if (!document.querySelector('.el-message-box__wrapper')) {
          MessageBox.confirm(
            '登录状态已过期，请重新登录',
            '权限提示',
            {
              confirmButtonText: '重新登录',
              cancelButtonText: '取消',
              type: 'warning',
              closeOnClickModal: false // 禁止点击空白处关闭
            }
          ).then(() => {
            router.push({ path: '/login', replace: true }); // 替换路由，防止回退
          }).catch(() => {});
        }
        // 强制reject，确保页面能捕获并处理
        return Promise.reject(new Error('Token已失效：401'));
      }

      // 5. 非200业务异常：仅弹错，不reject（保留原有逻辑）
      if (res.code !== 200) {
        // 轻量提示，避免频繁弹框（可根据业务调整）
        Message.warning(res.msg || '业务处理失败');
      }

      // 6. 所有情况都返回res，确保Promise必有状态
      return res;

    } catch (parseError) {
      // 核心兜底：JSON解析失败（如后端返回非JSON数据），必返回可处理对象
      console.error('【JSON解析失败】', parseError, response.data);
      Message.error('数据解析失败，请联系管理员');
      // 强制返回兜底数据，确保页面不卡死
      return { code: 500, data: {}, msg: '数据解析失败：' + parseError.message };
    }
  },
  error => {
    // 1. 立即清除所有pending请求，防止内存泄漏+请求阻塞
    pendingRequests.clear();
    // 2. 全维度错误分类处理，友好提示
    console.error('【响应拦截器异常】', error);
    let errorMsg = '请求失败，请检查网络';
    // 取消重复请求：不弹错，静默处理
    if (axios.isCancel(error)) {
      console.log('【请求已取消】', error.message);
      return Promise.reject(new Error('cancel'));
    }
    // 网络错误/超时
    else if (!error.response) {
      errorMsg = error.message.includes('timeout') 
        ? '请求超时，请检查网络或稍后重试' 
        : '网络异常，请检查WiFi/手机网络';
    }
    // HTTP状态码错误（404/500等）
    else {
      const status = error.response.status;
      switch (status) {
        case 404: errorMsg = '请求接口不存在，请联系管理员'; break;
        case 500: errorMsg = '服务器内部错误，请联系管理员'; break;
        case 403: errorMsg = '无权限访问，请联系管理员分配权限'; break;
        case 400: errorMsg = '请求参数错误，请检查筛选条件'; break;
        default: errorMsg = `请求失败：${status}错误`;
      }
    }
    // 3. 非取消类错误，统一弹提示
    if (!error.message.includes('cancel')) {
      Message.error(errorMsg);
    }
    // 4. 强制返回reject，确保页面catch能捕获，Promise不挂起
    return Promise.reject(new Error(errorMsg));
  }
);

// 新增：全局请求错误兜底（防止个别漏网之鱼）
service.defaults.validateStatus = () => true;

export default service;
export { isWxWorkEnv };